import { Component, type ReactNode, type ErrorInfo } from "react";
import { captureException } from "@services/config/sentry";
import { trackError } from "@services/analytics/analytics";

interface Props {
  children: ReactNode;
  /** Label shown in GA4 to identify which part of the UI crashed */
  component?: string;
}

interface State {
  hasError: boolean;
}

/**
 * React Error Boundary — catches render-phase errors and reports them to GA4.
 * Wrap any subtree that could throw during render (e.g. the full app in App.tsx).
 *
 * Note: Error Boundaries only catch synchronous render errors.
 * Async/Promise errors are captured globally in main.tsx via window.onerror.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    const component = this.props.component ?? info.componentStack?.split("\n")[1]?.trim() ?? "unknown";
    // Send full stack trace to Sentry
    captureException(error, { component, componentStack: info.componentStack });
    // Send lightweight event to GA4 for frequency metrics
    trackError({ description: error.message, component, fatal: true });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-gradient-to-b from-neutral-60 to-primary-110 px-4 text-center">
          <p className="text-white text-xl font-semibold">
            Algo salió mal
          </p>
          <p className="text-white/70 text-sm">
            Estamos trabajando para resolverlo. Intenta recargar la página.
          </p>
          <button
            className="mt-2 rounded-full bg-secondary-120 px-8 py-2 text-primary-110 font-semibold"
            onClick={() => window.location.reload()}
          >
            Recargar
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}
